import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useCapabilities } from '../../demo/capabilities';
import { LoadingStatus, useLoadingStore } from '../../store/LoadingStore';
import { useCompactUI } from './compactUi';

const DATA_PANEL_SELECTOR = '[data-guided-tour="guided-tour-data-panel"]';
const FILTERS_PANEL_SELECTOR = '[data-guided-tour="guided-tour-filters-panel"]';
const DESIGN_SCHEMES_PANEL_SELECTOR = '[data-guided-tour="guided-tour-design-schemes-panel"]';
const METRICS_PANEL_SELECTOR = '[data-guided-tour="guided-tour-metrics-panel"]';
const SCENE_SWITCHER_SELECTOR = '[data-guided-tour="guided-tour-scene-switcher"]';

export default function GuidedTour() {
    const { guidedTourEnabled } = useCapabilities();
    const loadingStatus = useLoadingStore((state) => state.loadingStatus);
    const isCompactUI = useCompactUI();
    const guidedTourRef = useRef<ReturnType<typeof driver> | null>(null);
    const hasStartedRef = useRef(false);

    useEffect(() => {
        if (!guidedTourEnabled || isCompactUI || loadingStatus !== LoadingStatus.VISUALIZER_READY || hasStartedRef.current) {
            return;
        }

        let attempts = 0;
        let timeoutId: number | null = null;
        let startDelayTimeoutId: number | null = null;

        const tryStartTour = () => {
            const dataPanel = document.querySelector(DATA_PANEL_SELECTOR);
            const filtersPanel = document.querySelector(FILTERS_PANEL_SELECTOR);
            const designSchemesPanel = document.querySelector(DESIGN_SCHEMES_PANEL_SELECTOR);
            const metricsPanel = document.querySelector(METRICS_PANEL_SELECTOR);
            const sceneSwitcher = document.querySelector(SCENE_SWITCHER_SELECTOR);

            if (!dataPanel || !filtersPanel || !designSchemesPanel || !metricsPanel || !sceneSwitcher) {
                attempts += 1;

                if (attempts >= 20) {
                    return;
                }

                timeoutId = window.setTimeout(tryStartTour, 200);
                return;
            }

            hasStartedRef.current = true;
            guidedTourRef.current = driver({
                showProgress: true,
                allowClose: true,
                overlayClickBehavior: 'close',
                overlayColor: '#1f2937',
                overlayOpacity: 0.32,
                stagePadding: 6,
                stageRadius: 14,
                popoverClass: 'um-guided-tour-popover',
                steps: [
                    {
                        element: DATA_PANEL_SELECTOR,
                        popover: {
                            title: 'Data',
                            description: 'Browse geodata and simulations, and switch layers from this panel.',
                            side: 'right',
                            align: 'start',
                        },
                    },
                    {
                        element: FILTERS_PANEL_SELECTOR,
                        popover: {
                            title: 'Filters',
                            description: 'Add new filters and run them in a pipeline.',
                            side: 'left',
                            align: 'start',
                        },
                    },
                    {
                        element: DESIGN_SCHEMES_PANEL_SELECTOR,
                        popover: {
                            title: 'Design Schemes',
                            description: 'Create and compare design options from this panel.',
                            side: 'left',
                            align: 'start',
                        },
                    },
                    {
                        element: METRICS_PANEL_SELECTOR,
                        popover: {
                            title: 'Metrics',
                            description: 'Review key performance metrics for the current selection here.',
                            side: 'left',
                            align: 'start',
                        },
                    },
                    {
                        element: SCENE_SWITCHER_SELECTOR,
                        popover: {
                            title: 'Project switcher',
                            description: 'Use these buttons to switch between projects.',
                            side: 'bottom',
                            align: 'center',
                        },
                    },
                ],
            });

            startDelayTimeoutId = window.setTimeout(() => {
                guidedTourRef.current?.drive();
            }, 600);
        };

        tryStartTour();

        return () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }

            if (startDelayTimeoutId) {
                window.clearTimeout(startDelayTimeoutId);
            }

            guidedTourRef.current?.destroy();
            guidedTourRef.current = null;
            hasStartedRef.current = false;
        };
    }, [guidedTourEnabled, isCompactUI, loadingStatus]);

    return null;
}
